import React, { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Linkedin, Sparkles, RefreshCw, Scissors, MapPin, Zap, X, Copy, Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const PLATFORMS = [{ n: "Instagram", I: Instagram }, { n: "Facebook", I: Facebook }, { n: "LinkedIn", I: Linkedin }];
const OBJECTIVES = ["Awareness", "Engagement", "Valuation leads", "Property promotion", "Local authority"];
const TONES = ["Our Brand Voice", "Professional", "Friendly", "Local expert", "Conversational"];

export default function ContentCreator({ asset, onClose, onAddToWeek }) {
  const [platform, setPlatform] = useState("Instagram");
  const [objective, setObjective] = useState("Property promotion");
  const [tone, setTone] = useState("Our Brand Voice");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async (variant) => {
    setLoading(true);
    try {
      const { data } = await api.post("/content/generate", {
        asset_id: asset.id, title: asset.title, area: asset.area, kind: asset.type,
        summary: asset.summary, media: asset.media, platform, objective, tone, variant,
      });
      setResult(data);
    } catch {
      toast.error("Couldn't generate content. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    if (!result) return;
    navigator.clipboard.writeText(`${result.caption}\n\n${(result.hashtags || []).join(" ")}`);
    toast.success("Caption copied");
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 z-40" style={{ background: "rgba(21,21,21,.35)" }} />
      <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-3xl bg-white border-l overflow-y-auto grid md:grid-cols-2" style={{ borderColor: "#E7E8E4" }} data-testid="content-creator">

        {/* Left — asset preview */}
        <div className="p-6 border-r hidden md:block" style={{ borderColor: "#E7E8E4", background: "#F7F8F6" }}>
          <span className="label-caps">Asset preview</span>
          <div className="mt-3 rounded-xl overflow-hidden border" style={{ borderColor: "#E7E8E4" }}>
            <img src={asset.image} alt={asset.title} className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="mt-4">
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.12)", color: "#678A16" }}>{asset.type}</span>
            <h3 className="font-bold text-ink mt-2">{asset.title}</h3>
            <p className="text-sm text-ink-2 mt-1">{asset.summary}</p>
            {asset.media?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {asset.media.map((m) => <span key={m} className="text-xs font-medium text-ink-2 rounded-full border px-2 py-0.5" style={{ borderColor: "#E7E8E4" }}>{m}</span>)}
              </div>
            )}
          </div>
        </div>

        {/* Right — controls */}
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold tracking-tight text-ink">Create post</h2>
            <button className="btn-ghost focus-ring" onClick={onClose} data-testid="close-creator"><X size={18} /></button>
          </div>

          <div className="mt-5">
            <span className="label-caps">Platform</span>
            <div className="flex gap-2 mt-2">
              {PLATFORMS.map(({ n, I }) => (
                <button key={n} onClick={() => setPlatform(n)} data-testid={`platform-${n.toLowerCase()}`}
                  className="flex-1 flex items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-medium transition-all"
                  style={platform === n ? { borderColor: "#93C020", background: "rgba(147,192,32,.06)", color: "#151515" } : { borderColor: "#E7E8E4", color: "#6B6B6B" }}>
                  <I size={16} strokeWidth={1.5} /> {n}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <span className="label-caps">Objective</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {OBJECTIVES.map((o) => (
                <button key={o} onClick={() => setObjective(o)} data-testid={`objective-${o}`}
                  className="rounded-full border px-3 py-1.5 text-sm font-medium transition-all"
                  style={objective === o ? { background: "#151515", color: "#fff", borderColor: "#151515" } : { borderColor: "#E7E8E4", color: "#6B6B6B" }}>{o}</button>
              ))}
            </div>
          </div>

          <div className="mt-5">
            <span className="label-caps">Tone</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {TONES.map((t) => (
                <button key={t} onClick={() => setTone(t)} data-testid={`tone-${t}`}
                  className="rounded-full border px-3 py-1.5 text-sm font-medium transition-all"
                  style={tone === t ? { background: "rgba(147,192,32,.1)", color: "#678A16", borderColor: "#93C020" } : { borderColor: "#E7E8E4", color: "#6B6B6B" }}>{t}</button>
              ))}
            </div>
          </div>

          {!result ? (
            <button className="btn-primary w-full mt-6 focus-ring !py-3" onClick={() => generate(null)} disabled={loading} data-testid="generate-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={16} strokeWidth={2} /> Generate content</>}
            </button>
          ) : (
            <div className="mt-6 animate-fade">
              {result.headline && <div className="mb-3"><span className="label-caps">Headline</span><p className="font-bold text-ink mt-1">{result.headline}</p></div>}
              <span className="label-caps">Caption</span>
              <div className="mt-1 rounded-lg border p-3.5 text-sm text-ink leading-relaxed whitespace-pre-wrap" style={{ borderColor: "#E7E8E4" }} data-testid="generated-caption">{result.caption}</div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(result.hashtags || []).map((h) => <span key={h} className="text-xs font-medium rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.1)", color: "#678A16" }}>{h}</span>)}
              </div>
              {result.cta && <p className="text-sm text-ink-2 mt-3"><span className="label-caps">CTA</span> <span className="ml-1 font-semibold text-ink">{result.cta}</span></p>}

              <div className="flex flex-wrap gap-2 mt-4">
                <button className="btn-secondary text-xs !py-1.5 focus-ring" onClick={() => generate("regenerate")} disabled={loading} data-testid="regenerate"><RefreshCw size={13} strokeWidth={1.75} /> Regenerate</button>
                <button className="btn-secondary text-xs !py-1.5 focus-ring" onClick={() => generate("shorter")} disabled={loading}><Scissors size={13} strokeWidth={1.75} /> Shorter</button>
                <button className="btn-secondary text-xs !py-1.5 focus-ring" onClick={() => generate("more_local")} disabled={loading}><MapPin size={13} strokeWidth={1.75} /> More local</button>
                <button className="btn-secondary text-xs !py-1.5 focus-ring" onClick={() => generate("more_engaging")} disabled={loading}><Zap size={13} strokeWidth={1.75} /> More engaging</button>
              </div>

              <div className="flex gap-2 mt-6">
                <button className="btn-secondary focus-ring" onClick={copyAll} data-testid="copy-caption"><Copy size={15} strokeWidth={1.75} /> Copy</button>
                <button className="btn-primary flex-1 focus-ring" onClick={() => { onAddToWeek?.({ ...asset, caption: result.caption, platform }); toast.success("Added to Social Week"); onClose(); }} data-testid="add-to-week">
                  <Plus size={16} strokeWidth={2} /> Add to Social Week
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}
